import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  History,
  LogOut,
  Minus,
  XCircle,
} from 'lucide-react';
import { StatusBadge } from '../../components/UI/StatusBadge';
import { CountBadge } from '../../components/UI/CountBadge';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import FormSelect from '../../components/UI/FormSelect';
import ConfirmModal from '../../components/UI/ConfirmModal';
import EventTimeline from '../../components/UI/EventTimeline';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  useApproveTermination,
  useHRTerminationRequest,
  useHRTerminationRequests,
  useRejectTermination,
  useWitnessedException,
} from '../../hooks/useTerminationRequests';
import { formatDate } from '../../utils/dateUtils';

const EXIT_TYPES = [
  { value: 'resignation', label: 'Resignation' },
  { value: 'termination_with_notice', label: 'Termination with notice' },
  { value: 'termination_without_notice', label: 'Termination without notice' },
  { value: 'summary_dismissal', label: 'Summary dismissal' },
  { value: 'contract_end', label: 'Contract end' },
  { value: 'retrenchment', label: 'Retrenchment' },
  { value: 'mutual_agreement', label: 'Mutual agreement' },
];

const OPEN_STATUSES = new Set([
  'awaiting_worker_acknowledgement',
  'awaiting_department_acknowledgement',
  'returned_for_correction',
  'disputed',
  'pending_hr_review',
]);

const isLegacyRow = (item) => item.source === 'legacy_xlsx' || item.status === 'legacy';

const VIEWS = [
  { id: 'open', label: 'Open' },
  { id: 'closed', label: 'Closed' },
  { id: 'legacy', label: 'Historical archive' },
];

const EVENT_LABELS = {
  created: 'Request created',
  worker_acknowledged: 'Worker acknowledged',
  disputed: 'Worker disputed',
  notice_corrected: 'Notice corrected',
  witnessed_exception: 'Witnessed exception recorded',
  department_acknowledged: 'Department acknowledged',
  returned_for_correction: 'Returned for correction',
  approved: 'HR approved',
  rejected: 'HR rejected',
  cancelled: 'Cancelled',
};

const LEGACY_CHECKLIST_SECTIONS = [
  {
    key: 'assets',
    title: 'Assets and returns',
    fields: [
      { key: 'items_to_return', label: 'Items recorded for return' },
      { key: 'other_items', label: 'Other items' },
      {
        key: 'all_returned',
        label: 'All property and equipment returned',
        boolean: true,
        positiveValue: true,
      },
    ],
  },
  {
    key: 'handover',
    title: 'Handover',
    fields: [
      {
        key: 'completed_or_transferred',
        label: 'Pending work completed or responsibilities transferred',
        boolean: true,
        positiveValue: true,
      },
    ],
  },
  {
    key: 'travel_and_work_pass',
    title: 'Travel and work pass',
    fields: [
      {
        key: 'flight_ready',
        label: 'Worker aware of flight and prepared to travel',
        boolean: true,
        positiveValue: true,
      },
      {
        key: 'work_permit_card_returned',
        label: 'Work Permit card returned',
        boolean: true,
        positiveValue: true,
      },
    ],
  },
  {
    key: 'feedback',
    title: 'Feedback',
    fields: [
      { key: 'offered', label: 'Worker chose to provide feedback', boolean: true },
      { key: 'comments', label: 'Recorded feedback' },
    ],
  },
  {
    key: 'finance_and_tax',
    title: 'Finance and tax',
    fields: [
      {
        key: 'outstanding_obligations',
        label: 'Outstanding financial obligations',
        boolean: true,
        positiveValue: false,
      },
      { key: 'ir21_required', label: 'IR21 filing required', boolean: true },
      { key: 'withholding_amount', label: 'Withholding monies amount' },
    ],
  },
];

const ArchiveAnswer = ({ value, boolean, positiveValue }) => {
  if (value === null || value === undefined || value === '') {
    return (
      <dd className="mt-1 flex items-center gap-1.5 text-sm text-gray-400">
        <Minus className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
        Not recorded
      </dd>
    );
  }
  if (boolean) {
    const isNeutral = positiveValue === undefined;
    const isPositive = value === positiveValue;
    if (isNeutral) {
      return (
        <dd className={`mt-1 flex items-center gap-1.5 text-sm font-medium ${
          value ? 'text-blue-700' : 'text-gray-600'
        }`}>
          {value ? (
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
          ) : (
            <XCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
          )}
          {value ? 'Yes' : 'No'}
        </dd>
      );
    }
    return isPositive ? (
      <dd className="mt-1 flex items-center gap-1.5 text-sm font-medium text-green-700">
        <CheckCircle2 className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
        {value ? 'Yes' : 'No'}
      </dd>
    ) : (
      <dd className="mt-1 flex items-center gap-1.5 text-sm font-medium text-red-700">
        <XCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
        {value ? 'Yes' : 'No'}
      </dd>
    );
  }
  return <dd className="mt-1 text-sm font-medium text-gray-900 whitespace-pre-wrap break-words">{String(value)}</dd>;
};

const TerminationRequestBoard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('id') || '';
  const view = VIEWS.some((item) => item.id === searchParams.get('view'))
    ? searchParams.get('view')
    : 'open';
  const isCompact = useIsMobile(1024);
  const listPaneRef = useRef(null);
  const detailPaneRef = useRef(null);
  const [exitType, setExitType] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [witness, setWitness] = useState({
    witness_2_staff_id: '',
    reason: '',
    interpreter_name: '',
    interpreter_language: '',
  });
  const [confirm, setConfirm] = useState(null);

  const { data: list = [], isLoading, isError, refetch } = useHRTerminationRequests();
  const { data: detail, isLoading: detailLoading } = useHRTerminationRequest(selectedId);
  const counts = useMemo(() => ({
    open: list.filter((item) => !isLegacyRow(item) && OPEN_STATUSES.has(item.status)).length,
    closed: list.filter((item) => !isLegacyRow(item) && !OPEN_STATUSES.has(item.status)).length,
    legacy: list.filter(isLegacyRow).length,
  }), [list]);
  const visibleList = useMemo(() => {
    if (view === 'legacy') return list.filter(isLegacyRow);
    if (view === 'closed') return list.filter((item) => !isLegacyRow(item) && !OPEN_STATUSES.has(item.status));
    return list.filter((item) => !isLegacyRow(item) && OPEN_STATUSES.has(item.status));
  }, [list, view]);

  const changeView = (nextView) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', nextView);
    next.delete('id');
    setSearchParams(next);
  };
  const approve = useApproveTermination();
  const reject = useRejectTermination();
  const witnessed = useWitnessedException();

  const openDetail = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('id', id);
    setSearchParams(next);
  };

  const closeCompact = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    setSearchParams(next);
  };

  useEffect(() => {
    if (isCompact || !selectedId || isLoading) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const pane = listPaneRef.current;
      const card = pane?.querySelector('[data-selected="true"]');
      if (!pane || !card) return;
      const paneRect = pane.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      if (cardRect.top < paneRect.top) {
        pane.scrollTop += cardRect.top - paneRect.top;
      } else if (cardRect.bottom > paneRect.bottom) {
        pane.scrollTop += cardRect.bottom - paneRect.bottom;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isCompact, selectedId, isLoading]);

  useEffect(() => {
    if (isCompact || !selectedId) return;
    detailPaneRef.current?.scrollTo({ top: 0 });
  }, [isCompact, selectedId]);

  const isLegacy = detail?.source === 'legacy_xlsx' || detail?.status === 'legacy';
  const canReview = ['pending_hr_review', 'disputed'].includes(detail?.status) && !isLegacy;
  const canWitness = detail?.status === 'awaiting_worker_acknowledgement' && !isLegacy;

  const emptyCopy = {
    open: {
      title: 'No open requests',
      body: 'New portal requests land here for HR review.',
    },
    closed: {
      title: 'No closed portal requests',
      body: 'Approved, rejected, or cancelled portal requests will appear here.',
    },
    legacy: {
      title: 'Historical archive is empty',
      body: 'Imported workbook rows will appear here as read-only history. They are not live cases.',
    },
  }[view];

  return (
    <div className="flex flex-col gap-4 lg:flex-1 lg:min-h-0 lg:overflow-hidden">
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide flex-shrink-0">
        {VIEWS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => changeView(item.id)}
            className={`tab-btn-admin ${view === item.id ? 'active' : ''}`}
          >
            {item.label}
            {counts[item.id] > 0 && (
              <CountBadge count={counts[item.id]} theme="admin" active={view === item.id} />
            )}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 flex justify-center lg:flex-1">
          <LoadingSpinner size="md" />
        </div>
      ) : isError ? (
        <div className="bg-white rounded-2xl border border-gray-200 lg:flex-1">
          <div className="text-center py-12 px-6">
            <LogOut className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Could not load requests</h3>
            <p className="text-sm text-gray-500">Try again. Nothing on this page was lost.</p>
            <button type="button" className="btn btn-admin-primary mt-4" onClick={() => refetch()}>
              Try again
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4 lg:gap-6 lg:flex-1 lg:min-h-0 lg:overflow-hidden">
          <div
            ref={listPaneRef}
            className={`${
              isCompact && selectedId ? 'hidden' : 'space-y-3'
            } lg:min-h-0 lg:overflow-y-auto lg:overscroll-y-contain lg:pr-1`}
          >
            {visibleList.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200">
                <div className="text-center py-12 px-6">
                  {view === 'legacy' ? (
                    <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  ) : (
                    <LogOut className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  )}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{emptyCopy.title}</h3>
                  <p className="text-sm text-gray-500 max-w-sm mx-auto">{emptyCopy.body}</p>
                </div>
              </div>
            ) : (
              visibleList.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  data-selected={selectedId === item.id ? 'true' : undefined}
                  onClick={() => openDetail(item.id)}
                  className={`w-full text-left bg-white rounded-2xl border p-4 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${
                    selectedId === item.id ? 'border-red-600 shadow-md' : 'border-gray-200 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{item.worker_name_snapshot}</p>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {item.request_number} · {item.source.replace(/_/g, ' ')}
                      </p>
                    </div>
                    <StatusBadge status={item.status} />
                  </div>
                  <p className="text-xs text-gray-500 mt-2 tabular-nums">
                    Proposed last day {formatDate(item.proposed_last_working_day)}
                  </p>
                </button>
              ))
            )}
          </div>

          <div
            ref={detailPaneRef}
            className={`${
              isCompact && !selectedId ? 'hidden' : ''
            } lg:min-h-0 lg:h-full lg:overflow-y-auto lg:overscroll-y-contain lg:pr-1`}
          >
            {isCompact && selectedId && (
              <button type="button" onClick={closeCompact} className="btn btn-admin-outline mb-3">
                <ArrowLeft className="w-4 h-4" />
                Back to list
              </button>
            )}
            {!selectedId ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-sm text-gray-500 lg:h-full lg:flex lg:flex-col lg:items-center lg:justify-center">
                Select a request to review it
              </div>
            ) : detailLoading || !detail ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 flex justify-center lg:h-full lg:items-center">
                <LoadingSpinner size="md" />
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{detail.worker_name_snapshot}</h2>
                    <p className="text-sm text-gray-500">{detail.request_number} · {detail.worker_staff_id}</p>
                  </div>
                  <StatusBadge status={detail.status} />
                </div>
                {isLegacy && (
                  <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-xl p-3">
                    Read-only historical import {detail.legacy_reference_id || ''}.
                    {detail.legacy_transfer_discontinued ? ' External-company transfer is discontinued and was not converted.' : ''}
                    {' '}Original status: {detail.legacy_status || '—'} · type: {detail.legacy_type || '—'}.
                  </p>
                )}
                {detail.source === 'work_pass_end' && (
                  <p className="text-sm text-red-800 bg-red-50 border border-red-100 rounded-xl p-3">
                    Generated from an approved work-pass end-at-expiry decision. Worker, reason, and date stay as recorded.
                  </p>
                )}
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-xs text-gray-500">Source</dt>
                    <dd className="font-medium text-gray-900">{detail.source.replace(/_/g, ' ')}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Reason</dt>
                    <dd className="font-medium text-gray-900">{detail.reason?.replace(/_/g, ' ') || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Notice date</dt>
                    <dd className="font-medium text-gray-900 tabular-nums">{formatDate(detail.notice_date)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Proposed last day</dt>
                    <dd className="font-medium text-gray-900 tabular-nums">{formatDate(detail.proposed_last_working_day)}</dd>
                  </div>
                </dl>
                {detail.reason_detail && (
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{detail.reason_detail}</p>
                )}

                {isLegacy && detail.legacy_checklist && (
                  <section className="border-t border-gray-100 pt-4" aria-labelledby="legacy-checklist-heading">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <History className="w-4 h-4 text-gray-500" aria-hidden="true" />
                      </div>
                      <div className="min-w-0">
                        <h3 id="legacy-checklist-heading" className="text-sm font-semibold text-gray-900">
                          Archived offboarding checklist
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Evidence recorded in the legacy workbook. These are not live workflow tasks.
                        </p>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-xl px-4">
                      {LEGACY_CHECKLIST_SECTIONS.map((section, index) => (
                        <div
                          key={section.key}
                          className={`py-4 ${index < LEGACY_CHECKLIST_SECTIONS.length - 1 ? 'border-b border-gray-200' : ''}`}
                        >
                          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                            {section.title}
                          </h4>
                          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                            {section.fields.map((field) => (
                              <div key={field.key} className={field.key === 'comments' ? 'sm:col-span-2' : ''}>
                                <dt className="text-xs text-gray-500">{field.label}</dt>
                                <ArchiveAnswer
                                  value={detail.legacy_checklist[section.key]?.[field.key]}
                                  boolean={field.boolean}
                                  positiveValue={field.positiveValue}
                                />
                              </div>
                            ))}
                          </dl>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {canReview && (
                  <div className="space-y-3 border-t border-gray-100 pt-4">
                    <FormSelect
                      theme="admin"
                      options={EXIT_TYPES}
                      value={exitType}
                      onChange={setExitType}
                      placeholder="Canonical exit type"
                    />
                    <button
                      type="button"
                      className="btn btn-admin-primary w-full"
                      disabled={!exitType || approve.isPending}
                      onClick={() => setConfirm({ kind: 'approve' })}
                    >
                      Approve and create offboarding case
                    </button>
                    <textarea
                      className="input-admin w-full"
                      rows={2}
                      placeholder="Rejection reason"
                      value={rejectReason}
                      onChange={(event) => setRejectReason(event.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-admin-outline w-full"
                      disabled={!rejectReason.trim() || reject.isPending}
                      onClick={() => setConfirm({ kind: 'reject' })}
                    >
                      Reject
                    </button>
                  </div>
                )}

                {canWitness && (
                  <div className="space-y-3 border-t border-gray-100 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900">Two-HR witnessed exception</h3>
                    <input
                      className="input-admin w-full"
                      placeholder="Second HR witness staff ID"
                      value={witness.witness_2_staff_id}
                      onChange={(event) => setWitness((current) => ({ ...current, witness_2_staff_id: event.target.value }))}
                    />
                    <textarea
                      className="input-admin w-full"
                      rows={2}
                      placeholder="Reason / interpreter note"
                      value={witness.reason}
                      onChange={(event) => setWitness((current) => ({ ...current, reason: event.target.value }))}
                    />
                    <input
                      className="input-admin w-full"
                      placeholder="Interpreter name (optional)"
                      value={witness.interpreter_name}
                      onChange={(event) => setWitness((current) => ({ ...current, interpreter_name: event.target.value }))}
                    />
                    <input
                      className="input-admin w-full"
                      placeholder="Interpreter language (optional)"
                      value={witness.interpreter_language}
                      onChange={(event) => setWitness((current) => ({ ...current, interpreter_language: event.target.value }))}
                    />
                    <button
                      type="button"
                      className="btn btn-admin-primary w-full"
                      disabled={!witness.witness_2_staff_id.trim() || !witness.reason.trim() || witnessed.isPending}
                      onClick={() => setConfirm({ kind: 'witness' })}
                    >
                      Record witnessed acknowledgement
                    </button>
                  </div>
                )}

                {detail.offboarding_workflow_id && (
                  <button
                    type="button"
                    className="btn btn-admin-outline w-full"
                    onClick={() => navigate(`/admin/employee-lifecycle/offboarding/${detail.offboarding_workflow_id}`)}
                  >
                    Open offboarding case
                  </button>
                )}

                <EventTimeline
                  events={(detail.events || []).map((event) => ({
                    id: event.id,
                    label: EVENT_LABELS[event.event_type] || event.event_type,
                    subtitle: [event.actor_name, event.reason].filter(Boolean).join(' · '),
                    timestamp: event.created_at,
                    color: event.event_type === 'rejected' || event.event_type === 'disputed' ? 'red' : 'red',
                  }))}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirm?.kind === 'approve'}
        theme="admin"
        variant="primary"
        title="Create the offboarding case"
        confirmLabel="Approve"
        isLoading={approve.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => approve.mutate(
          { id: selectedId, data: { exit_type: exitType } },
          {
            onSuccess: (response) => {
              setConfirm(null);
              const workflowId = response.data?.offboarding_workflow_id;
              if (workflowId) {
                navigate(`/admin/employee-lifecycle/offboarding/${workflowId}`);
              }
            },
          },
        )}
      >
        <p className="text-sm text-gray-700">
          This calls the existing offboarding case factory. It does not set dates or notify the worker until the existing plan is released.
        </p>
      </ConfirmModal>

      <ConfirmModal
        isOpen={confirm?.kind === 'reject'}
        theme="admin"
        variant="danger"
        title="Reject this request"
        confirmLabel="Reject"
        isLoading={reject.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => reject.mutate(
          { id: selectedId, data: { reason: rejectReason } },
          { onSuccess: () => setConfirm(null) },
        )}
      >
        <p className="text-sm text-gray-700">
          A work-pass-origin rejection returns that cycle for a new recommendation.
        </p>
      </ConfirmModal>

      <ConfirmModal
        isOpen={confirm?.kind === 'witness'}
        theme="admin"
        variant="danger"
        title="Record a two-HR witnessed acknowledgement"
        confirmLabel="Record"
        isLoading={witnessed.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => witnessed.mutate(
          { id: selectedId, data: witness },
          { onSuccess: () => setConfirm(null) },
        )}
      >
        <p className="text-sm text-gray-700">
          Use only when the worker cannot acknowledge in the portal. Two distinct HR witnesses are required. Leaders cannot proxy this.
        </p>
      </ConfirmModal>
    </div>
  );
};

export default TerminationRequestBoard;
